import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Leaf, Award, Globe, Zap, Heart, Sparkles } from 'lucide-react';

export const Sostenibilita = () => {
  const principles = [
    {
      icon: Leaf,
      title: 'Economia Circolare',
      description:
        'Trasformiamo materiali di scarto in risorse preziose, chiudendo il ciclo dei materiali e riducendo la necessità di materie prime vergini.',
    },
    {
      icon: Globe,
      title: 'Riduzione Impatto Ambientale',
      description:
        'Il nostro processo di riciclo permette di evitare lo smaltimento in discarica e riduce significativamente le emissioni di CO2 rispetto alla produzione primaria.',
    },
    {
      icon: Zap,
      title: 'Efficienza Energetica',
      description:
        'Il recupero del rame richiede fino al 85% di energia in meno rispetto all\'estrazione e alla lavorazione del minerale grezzo.',
    },
  ];

  const benefits = [
    {
      title: 'Conservazione Risorse Naturali',
      description: 'Ogni tonnellata di rame riciclato preserva le riserve minerarie del pianeta',
      icon: '🌍',
    },
    {
      title: 'Riduzione Emissioni CO2',
      description: 'Contribuiamo attivamente alla lotta contro il cambiamento climatico',
      icon: '🌱',
    },
    {
      title: 'Economia Locale',
      description: 'Creiamo valore sul territorio attraverso il recupero di materiali',
      icon: '🏢',
    },
    {
      title: 'Materiali Strategici',
      description: 'Il rame è fondamentale per la transizione energetica e l\'elettrificazione',
      icon: '⚡',
    },
  ];

  const certifications = [
    {
      name: 'ISO 14001',
      description: 'Gestione ambientale',
      status: 'in-progress',
    },
    {
      name: 'EuCertPlast',
      description: 'Riciclo materie plastiche',
      status: 'in-progress',
    },
    {
      name: 'End of Waste',
      description: 'Cessazione qualifica rifiuto',
      status: 'in-progress',
    },
  ];

  return (
    <div className="pt-20">
      {/* Hero Section Premium */}
      <section className="relative py-24 bg-gradient-to-br from-[var(--verdigris)] to-[var(--verdigris-dark)] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-20 w-96 h-96 bg-white rounded-full blur-3xl animate-float"></div>
          <div className="absolute bottom-20 left-20 w-96 h-96 bg-white rounded-full blur-3xl animate-float" style={{ animationDelay: '1.5s' }}></div>
        </div>
        <div className="absolute inset-0 pattern-dots opacity-20"></div>
        
        <div className="container relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 mb-6 bg-white/10 backdrop-blur-sm rounded-full border border-white/20">
              <Leaf className="h-4 w-4" />
              <span className="text-sm font-semibold">Economia Circolare</span>
            </div>
            <h1 className="text-6xl font-bold mb-6 animate-fade-in">Sostenibilità</h1>
            <p className="text-xl text-white/90 animate-fade-in leading-relaxed" style={{ animationDelay: '0.1s' }}>
              Il nostro impegno per l'ambiente e l'economia circolare
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-24 bg-gradient-to-b from-white to-[var(--warm-gray)]">
        <div className="container">
          <div className="max-w-5xl mx-auto text-center">
            <h2 className="text-5xl font-bold mb-8">La Nostra Missione Ambientale</h2>
            <p className="text-xl text-[var(--text-light)] leading-relaxed mb-12">
              La missione di FIRM s.a.s. è promuovere il <strong className="text-[var(--copper-primary)]">recupero e la reintroduzione
              dei materiali</strong>, in particolare metalli e polimeri da cavi elettrici, nei cicli produttivi secondo i principi
              dell'<strong className="text-[var(--verdigris)]">economia circolare</strong>, riducendo al minimo l'impatto ambientale.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <Card className="text-center p-8 border-2 hover:border-[var(--copper-primary)] transition-all duration-300 hover:shadow-xl hover:-translate-y-2">
                <CardContent className="p-0">
                  <div className="text-6xl mb-4">♻️</div>
                  <h3 className="text-2xl font-bold mb-3">Recupero</h3>
                  <p className="text-[var(--text-light)] leading-relaxed">Materiali di scarto trasformati in risorse</p>
                </CardContent>
              </Card>
              <Card className="text-center p-8 border-2 hover:border-[var(--verdigris)] transition-all duration-300 hover:shadow-xl hover:-translate-y-2">
                <CardContent className="p-0">
                  <div className="text-6xl mb-4">🔄</div>
                  <h3 className="text-2xl font-bold mb-3">Reintroduzione</h3>
                  <p className="text-[var(--text-light)] leading-relaxed">Nuova vita nei cicli produttivi</p>
                </CardContent>
              </Card>
              <Card className="text-center p-8 border-2 hover:border-[var(--copper-primary)] transition-all duration-300 hover:shadow-xl hover:-translate-y-2">
                <CardContent className="p-0">
                  <div className="text-6xl mb-4">🌍</div>
                  <h3 className="text-2xl font-bold mb-3">Tutela</h3>
                  <p className="text-[var(--text-light)] leading-relaxed">Riduzione dell'impatto ambientale</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Principles Section */}
      <section className="py-24 bg-white">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold mb-6">I Nostri Principi</h2>
            <p className="text-xl text-[var(--text-light)] leading-relaxed">
              Sostenibilità e innovazione al centro della nostra attività
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {principles.map((principle, index) => {
              const Icon = principle.icon;
              return (
                <Card
                  key={index}
                  className="bg-white border-2 border-gray-200 hover:border-[var(--verdigris)] transition-all duration-500 hover:shadow-xl hover:-translate-y-2 group"
                >
                  <CardContent className="p-8">
                    <div className="w-20 h-20 bg-gradient-to-br from-[var(--verdigris)] to-[var(--verdigris-dark)] rounded-2xl flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300">
                      <Icon className="h-10 w-10 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold mb-4 group-hover:text-[var(--verdigris)] transition-colors">{principle.title}</h3>
                    <p className="text-[var(--text-light)] leading-relaxed">{principle.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 bg-gradient-to-b from-[var(--warm-gray)] to-white">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold mb-6">Impatto Positivo</h2>
            <p className="text-xl text-[var(--text-light)] leading-relaxed">
              I benefici del nostro lavoro per l'ambiente e la società
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {benefits.map((benefit, index) => (
              <Card
                key={index}
                className="border-2 hover:border-[var(--copper-primary)] transition-all duration-300 hover:shadow-xl hover:-translate-y-2"
              >
                <CardContent className="p-8">
                  <div className="flex items-start gap-6">
                    <div className="text-5xl flex-shrink-0">{benefit.icon}</div>
                    <div>
                      <h3 className="text-xl font-bold mb-3">{benefit.title}</h3>
                      <p className="text-[var(--text-light)] leading-relaxed">{benefit.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Copper Importance Section */}
      <section className="py-24 bg-gradient-to-br from-[var(--copper-primary)] to-[var(--copper-dark)] text-white relative overflow-hidden">
        <div className="absolute inset-0 pattern-dots opacity-10"></div>
        <div className="container relative z-10">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-5xl font-bold mb-8">Il Rame: Materiale Strategico</h2>
              <p className="text-xl text-white/90 leading-relaxed">
                Il rame è essenziale per la transizione energetica e un futuro sostenibile
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-white/10 backdrop-blur-md rounded-3xl p-10 border border-white/20 hover:bg-white/15 transition-all duration-300">
                <h3 className="text-3xl font-bold mb-6">Transizione Energetica</h3>
                <p className="text-white/90 leading-relaxed mb-6 text-lg">
                  Il rame è fondamentale per:
                </p>
                <ul className="space-y-3 text-white/90 text-lg">
                  <li className="flex items-center gap-3">
                    <span className="text-2xl">•</span>
                    <span>Reti elettriche e smart grid</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="text-2xl">•</span>
                    <span>Veicoli elettrici</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="text-2xl">•</span>
                    <span>Energie rinnovabili (solare, eolico)</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="text-2xl">•</span>
                    <span>Infrastrutture di ricarica</span>
                  </li>
                </ul>
              </div>
              <div className="bg-white/10 backdrop-blur-md rounded-3xl p-10 border border-white/20 hover:bg-white/15 transition-all duration-300">
                <h3 className="text-3xl font-bold mb-6">Riciclo Infinito</h3>
                <p className="text-white/90 leading-relaxed mb-6 text-lg">
                  Vantaggi del rame riciclato:
                </p>
                <ul className="space-y-3 text-white/90 text-lg">
                  <li className="flex items-center gap-3">
                    <span className="text-2xl">•</span>
                    <span>100% riciclabile senza perdita di qualità</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="text-2xl">•</span>
                    <span>85% di risparmio energetico</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="text-2xl">•</span>
                    <span>Riduzione drastica emissioni CO2</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="text-2xl">•</span>
                    <span>Preservazione risorse minerarie</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Certifications Section */}
      <section className="py-24 bg-gradient-to-b from-white to-[var(--warm-gray)]">
        <div className="container">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-5xl font-bold mb-6">Certificazioni Future</h2>
              <p className="text-xl text-[var(--text-light)] leading-relaxed">
                Il nostro percorso verso l'eccellenza nella gestione ambientale
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {certifications.map((cert, index) => (
                <Card
                  key={index}
                  className="border-2 border-gray-200 hover:border-[var(--copper-primary)] transition-all duration-500 hover:shadow-xl hover:-translate-y-2"
                >
                  <CardContent className="p-8 text-center">
                    <Award className="h-20 w-20 text-[var(--verdigris)] mx-auto mb-6" />
                    <h3 className="text-2xl font-bold mb-3">{cert.name}</h3>
                    <p className="text-[var(--text-light)] mb-6 leading-relaxed">{cert.description}</p>
                    <span className="inline-block px-6 py-3 bg-yellow-100 text-yellow-800 rounded-full text-sm font-semibold">
                      In fase di conseguimento
                    </span>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="mt-16 text-center">
              <p className="text-lg text-[var(--text-light)] leading-relaxed max-w-3xl mx-auto">
                Stiamo lavorando per ottenere le certificazioni ambientali più importanti del settore,
                confermando il nostro impegno verso la qualità, la trasparenza e la sostenibilità.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Commitment Section */}
      <section className="py-24 bg-gradient-to-br from-[var(--verdigris)] to-[var(--verdigris-dark)] text-white relative overflow-hidden">
        <div className="absolute inset-0 pattern-dots opacity-10"></div>
        <div className="container relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <Heart className="h-20 w-20 text-white mx-auto mb-8" />
            <h2 className="text-5xl font-bold mb-8">Il Nostro Impegno</h2>
            <p className="text-xl text-white/90 leading-relaxed">
              Come azienda familiare nata nel 2025, portiamo avanti la nostra attività con passione e dedizione,
              convinti che il riciclo e l'economia circolare siano la chiave per un futuro sostenibile.
              Ogni giorno lavoriamo per generare <strong className="text-white">valore condiviso</strong>:
              tutela ambientale, competitività economica e risposta alla crescente domanda di materiali strategici.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};